import { FRAUD_LOCALES, FRAUD_TIMEZONES } from 'constants/fraud';
import { getNavigatorLanguage } from 'utils/shared/time/getNavigatorLanguage';
import { getTimezone } from 'utils/shared/time/getTimezone';

export interface FraudDetectionResult {
  isFraud: boolean;
  reason?: string;
  timezone?: string;
  locale?: string;
}

export const detectFraudFromBrowser = (): FraudDetectionResult => {
  const timezone = getTimezone();
  const locale = getNavigatorLanguage();

  // Browser-side should also require this data to be consistent with server-side
  // and catch potential bypass attempts early
  if (!timezone || timezone.trim() === '') {
    return {
      isFraud: true,
      reason: 'Unable to detect timezone - please refresh and try again',
      timezone,
      locale,
    };
  }

  if (!locale || locale.trim() === '') {
    return {
      isFraud: true,
      reason: 'Unable to detect locale - please refresh and try again',
      timezone,
      locale,
    };
  }

  // Check for fraud timezones
  if (FRAUD_TIMEZONES.includes(timezone)) {
    return {
      isFraud: true,
      reason: `Suspicious timezone detected: ${timezone}`,
      timezone,
      locale,
    };
  }

  // Check for fraud locales
  if (FRAUD_LOCALES.some((fraudLocale) => locale.startsWith(fraudLocale))) {
    return {
      isFraud: true,
      reason: `Suspicious locale detected: ${locale}`,
      timezone,
      locale,
    };
  }

  return {
    isFraud: false,
    timezone,
    locale,
  };
};

export const detectFraudFromData = (timezone?: string, locale?: string): FraudDetectionResult => {
  // Server-side is stricter: require both timezone and locale to prevent API bypass attempts
  // Real browser clients should always send this data unless there's a legitimate technical issue

  if (!timezone || timezone.trim() === '' || !locale || locale.trim() === '') {
    return {
      isFraud: true,
      reason: 'Missing timezone or locale data - potential API bypass attempt',
      timezone,
      locale,
    };
  }

  // Basic format validation to catch obvious bypass attempts
  if (timezone.length < 3 || locale.length < 2) {
    return {
      isFraud: true,
      reason: 'Invalid timezone or locale format - potential bypass attempt',
      timezone,
      locale,
    };
  }

  // Check for fraud timezones
  if (FRAUD_TIMEZONES.includes(timezone)) {
    return {
      isFraud: true,
      reason: `Suspicious timezone detected: ${timezone}`,
      timezone,
      locale,
    };
  }

  // Check for fraud locales
  if (FRAUD_LOCALES.some((fraudLocale) => locale.startsWith(fraudLocale))) {
    return {
      isFraud: true,
      reason: `Suspicious locale detected: ${locale}`,
      timezone,
      locale,
    };
  }

  return {
    isFraud: false,
    timezone,
    locale,
  };
};
